import React, { useState, useEffect } from 'react';
import styles from './DuplicateInspectorModal.module.css';
import { findDuplicates, mergeDuplicateAttributes, extractFeatureName } from '../duplicates/duplicateDetector';
import historyManager from '../history/historyManager';
import { toast } from 'react-toastify';

export const DuplicateInspectorModal = ({
    isOpen,
    onClose,
    layersData = {},
    onDeleteObject,
    onUpdateObject,
    onZoomToObject
}) => {
    const [duplicates, setDuplicates] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isScanning, setIsScanning] = useState(false);
    const [minOverlap] = useState(80);

    // Сканирование при открытии
    useEffect(() => {
        if (isOpen) {
            setIsScanning(true);
            setTimeout(() => {
                const found = findDuplicates(layersData, { minOverlapPercent: minOverlap });
                setDuplicates(found);
                setSelectedIndex(0);
                setIsScanning(false);
            }, 50);
        }
    }, [isOpen, layersData, minOverlap]);

    const activePair = duplicates[selectedIndex] || null;

    if (!isOpen) return null;

    const handleDeleteDuplicate = async () => {
        if (!activePair) return;
        const { item2 } = activePair;

        try {
            // Сохраняем снимок в историю перед удалением
            historyManager.pushAction({
                type: 'delete_duplicate',
                description: `Удаление дубликата ${extractFeatureName(item2.feature)}`,
                before: [{ layer: item2.layer, id: item2.id, feature: item2.feature }],
                after: [{ layer: item2.layer, id: item2.id, isDeleted: true }]
            });

            if (onDeleteObject) {
                await onDeleteObject(item2.layer, item2.id);
            }

            toast.success(`Дубликат ${extractFeatureName(item2.feature)} успешно удален`);

            // Удаляем пару из списка
            const nextList = duplicates.filter((_, idx) => idx !== selectedIndex);
            setDuplicates(nextList);
            if (selectedIndex >= nextList.length) {
                setSelectedIndex(Math.max(0, nextList.length - 1));
            }
        } catch (err) {
            toast.error(`Ошибка при удалении дубликата: ${err.message}`);
        }
    };

    const handleMergeDuplicate = async () => {
        if (!activePair) return;
        const { item1, item2 } = activePair;

        try {
            const mergedFeature = mergeDuplicateAttributes(item1.feature, item2.feature);

            // Сохраняем снимок в историю
            historyManager.pushAction({
                type: 'merge_duplicate',
                description: `Слияние данных дубликатов ${extractFeatureName(item1.feature)}`,
                before: [
                    { layer: item1.layer, id: item1.id, feature: item1.feature },
                    { layer: item2.layer, id: item2.id, feature: item2.feature }
                ],
                after: [
                    { layer: item1.layer, id: item1.id, feature: mergedFeature },
                    { layer: item2.layer, id: item2.id, isDeleted: true }
                ]
            });

            // Обновляем мастер-объект и удаляем дубликат
            if (onUpdateObject) {
                await onUpdateObject(item1.layer, item1.id, mergedFeature);
            }
            if (onDeleteObject) {
                await onDeleteObject(item2.layer, item2.id);
            }

            toast.success(`Данные объединены в объект ${extractFeatureName(item1.feature)}`);

            const nextList = duplicates.filter((_, idx) => idx !== selectedIndex);
            setDuplicates(nextList);
            if (selectedIndex >= nextList.length) {
                setSelectedIndex(Math.max(0, nextList.length - 1));
            }
        } catch (err) {
            toast.error(`Ошибка при слиянии: ${err.message}`);
        }
    };

    const handleSkip = () => {
        const nextList = duplicates.filter((_, idx) => idx !== selectedIndex);
        setDuplicates(nextList);
        if (selectedIndex >= nextList.length) {
            setSelectedIndex(Math.max(0, nextList.length - 1));
        }
    };

    return (
        <div className={styles.backdrop} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerTitle}>
                        <span className={styles.titleText}>Инспектор дубликатов (SPD+)</span>
                        {duplicates.length > 0 ? (
                            <span className={styles.badgeCount}>Найдено дублей: {duplicates.length}</span>
                        ) : (
                            <span className={styles.badgeSuccess}>Дубликатов не найдено</span>
                        )}
                    </div>
                    <button className={styles.closeButton} onClick={onClose}>&times;</button>
                </div>

                {/* Body */}
                <div className={styles.body}>
                    {/* Left list */}
                    <div className={styles.listPanel}>
                        {isScanning ? (
                            <div className={styles.emptyState}>
                                <p>Сканирование базы объектов...</p>
                            </div>
                        ) : duplicates.length === 0 ? (
                            <div className={styles.emptyState}>
                                <p style={{ fontSize: '32px', marginBottom: '8px' }}>🎉</p>
                                <h4>Дубликаты отсутствуют</h4>
                                <p style={{ fontSize: '12px', marginTop: '4px' }}>Все контуры и кадастровые номера уникальны.</p>
                            </div>
                        ) : (
                            duplicates.map((pair, idx) => {
                                const isSelected = idx === selectedIndex;
                                return (
                                    <div
                                        key={pair.id}
                                        className={`${styles.listItem} ${isSelected ? styles.listItemSelected : ''}`}
                                        onClick={() => setSelectedIndex(idx)}
                                    >
                                        <div className={styles.listItemHeader}>
                                            <span className={styles.listItemTitle}>Пара #{idx + 1}: {extractFeatureName(pair.item1.feature)}</span>
                                            <span className={styles.cardBadgeMaster}>{pair.item1.layer}</span>
                                        </div>
                                        <span className={styles.listItemReason}>{pair.reason}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Right diff panel */}
                    <div className={styles.diffPanel}>
                        {activePair ? (
                            <>
                                <div className={styles.diffGrid}>
                                    {/* Master card */}
                                    <div className={styles.cardMaster}>
                                        <div className={styles.cardHeader}>
                                            <span className={styles.cardBadgeMaster}>Объект А (Основной)</span>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>ID: {activePair.item1.id}</span>
                                        </div>

                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Название:</span>
                                            <span className={styles.attrVal}>{activePair.diffs.name.val1}</span>
                                        </div>
                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Слой:</span>
                                            <span className={styles.attrVal}>{activePair.item1.layer}</span>
                                        </div>
                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Кадастровый №:</span>
                                            <span className={`${styles.attrVal} ${activePair.diffs.cadastral.isDiff ? styles.attrDiff : ''}`}>
                                                {activePair.diffs.cadastral.val1}
                                            </span>
                                        </div>
                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Площадь:</span>
                                            <span className={`${styles.attrVal} ${activePair.diffs.area.isDiff ? styles.attrDiff : ''}`}>
                                                {activePair.diffs.area.val1} м²
                                            </span>
                                        </div>
                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Категория:</span>
                                            <span className={`${styles.attrVal} ${activePair.diffs.category.isDiff ? styles.attrDiff : ''}`}>
                                                {activePair.diffs.category.val1}
                                            </span>
                                        </div>
                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Статус:</span>
                                            <span className={`${styles.attrVal} ${activePair.diffs.status.isDiff ? styles.attrDiff : ''}`}>
                                                {activePair.diffs.status.val1}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Duplicate card */}
                                    <div className={styles.cardDuplicate}>
                                        <div className={styles.cardHeader}>
                                            <span className={styles.cardBadgeDuplicate}>Объект Б (Дубликат)</span>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>ID: {activePair.item2.id}</span>
                                        </div>

                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Название:</span>
                                            <span className={styles.attrVal}>{activePair.diffs.name.val2}</span>
                                        </div>
                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Слой:</span>
                                            <span className={styles.attrVal}>{activePair.item2.layer}</span>
                                        </div>
                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Кадастровый №:</span>
                                            <span className={`${styles.attrVal} ${activePair.diffs.cadastral.isDiff ? styles.attrDiff : ''}`}>
                                                {activePair.diffs.cadastral.val2}
                                            </span>
                                        </div>
                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Площадь:</span>
                                            <span className={`${styles.attrVal} ${activePair.diffs.area.isDiff ? styles.attrDiff : ''}`}>
                                                {activePair.diffs.area.val2} м²
                                                {activePair.diffs.area.isDiff && (
                                                    <span style={{ fontSize: '11px', marginLeft: '6px' }}>
                                                        (Δ {activePair.diffs.area.delta > 0 ? '+' : ''}{activePair.diffs.area.delta} м²)
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Категория:</span>
                                            <span className={`${styles.attrVal} ${activePair.diffs.category.isDiff ? styles.attrDiff : ''}`}>
                                                {activePair.diffs.category.val2}
                                            </span>
                                        </div>
                                        <div className={styles.attrRow}>
                                            <span className={styles.attrLabel}>Статус:</span>
                                            <span className={`${styles.attrVal} ${activePair.diffs.status.isDiff ? styles.attrDiff : ''}`}>
                                                {activePair.diffs.status.val2}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ fontSize: '13px', color: '#64748b', background: 'rgba(0,0,0,0.03)', padding: '12px 16px', borderRadius: '8px' }}>
                                    💡 <strong>Причина совпадения:</strong> {activePair.reason}. Вы можете удалить дублирующий объект Б или перенести его заполненные характеристики в объект А.
                                </div>
                            </>
                        ) : (
                            <div className={styles.emptyState}>
                                <p>Выберите пару дубликатов слева для сравнения</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    {activePair ? (
                        <div className={styles.actionBtnGroup}>
                            {onZoomToObject && (
                                <button
                                    className={styles.btnSkip}
                                    onClick={() => onZoomToObject(activePair.item1.feature)}
                                    title="Показать объект на карте"
                                >
                                    📍 Показать на карте
                                </button>
                            )}
                            <button className={styles.btnSkip} onClick={handleSkip}>
                                👁️ Пропустить пару
                            </button>
                        </div>
                    ) : <div />}

                    {activePair && (
                        <div className={styles.actionBtnGroup}>
                            <button
                                className={styles.btnMerge}
                                onClick={handleMergeDuplicate}
                                title="Объединить свойства и оставить геометрию объекта А"
                            >
                                🔀 Объединить данные (Merge)
                            </button>
                            <button
                                className={styles.btnDelete}
                                onClick={handleDeleteDuplicate}
                                title="Удалить объект Б из базы данных"
                            >
                                🗑️ Удалить дубль (Б)
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DuplicateInspectorModal;
